import { useEffect, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { siteInfo } from "@/mocks/site";

interface NavLink {
  label: string;
  to: string;
  hash: string;
}

const navLinks: NavLink[] = [
  { label: "首页", to: "/", hash: "" },
  { label: "实时爆料", to: "/#live", hash: "live" },
  { label: "晚间热搜", to: "/#evening", hash: "evening" },
  { label: "分类专题", to: "/topics", hash: "" },
  { label: "关于我们", to: "/about", hash: "" },
];

export default function TopNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const handleHashClick = (event: MouseEvent<HTMLAnchorElement>, hash: string) => {
    event.preventDefault();
    setOpen(false);
    const doScroll = () => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    if (location.pathname === "/") {
      doScroll();
    } else {
      navigate("/");
      window.setTimeout(doScroll, 280);
    }
  };

  const isActive = (link: NavLink) => {
    if (link.hash) return false;
    if (link.to === "/") return location.pathname === "/";
    return location.pathname.startsWith(link.to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-300/70"
          : "bg-background-50/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-8" aria-label="主导航">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group" aria-label={`${siteInfo.name} 首页`}>
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg font-bold">
              爆
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="mt-0.5 text-[10px] tracking-[0.22em] text-foreground-500">
                REALTIME HOT
              </span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              link.hash ? (
                <a
                  key={link.label}
                  href={link.to}
                  onClick={(event) => handleHashClick(event, link.hash)}
                  className="px-3.5 py-2 rounded-md text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`px-3.5 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                    isActive(link)
                      ? "text-primary-600 bg-primary-50 font-medium"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                  }`}
                >
                  {link.label}
                </Link>
              ),
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/topics"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-fire-line" aria-hidden="true"></i>
              </span>
              进入热搜榜
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer"
            aria-label={open ? "关闭菜单" : "打开菜单"}
            aria-expanded={open}
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} aria-hidden="true"></i>
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 pt-1">
            <div className="flex flex-col rounded-lg border border-background-300/70 bg-background-100 p-2">
              {navLinks.map((link) =>
                link.hash ? (
                  <a
                    key={link.label}
                    href={link.to}
                    onClick={(event) => handleHashClick(event, link.hash)}
                    className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.to}
                    className={`px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                      isActive(link)
                        ? "bg-primary-50 text-primary-600 font-medium"
                        : "text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
                    }`}
                  >
                    {link.label}
                  </Link>
                ),
              )}
              <Link
                to="/topics"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                </span>
                进入热搜榜
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}